/**
 * What deleting an account says before the password is typed, per kind of
 * account. **The source both apps copy** (`MemberCopy` in iOS and Android for
 * the member's): one deletion, one list, on every surface that offers the
 * button.
 */
export interface DeletionCopy {
  /** The line under the heading, before the form is opened. */
  summary: string;
  sections: { title: string; items: string[] }[];
  /** Said last, in the ink colour, right above the password. */
  finalLine: string;
}

/**
 * A Consumer's (H34, Option A: anonymise and keep). Condition 3 of the ruling
 * is that this is said **before** the button, not after.
 */
export const CONSUMER_DELETION: DeletionCopy = {
  summary: "Your appointments stay in the clinic’s records, without your name.",
  sections: [
    {
      title: "What is deleted",
      items: [
        "Your name, your phone number and your password.",
        "Every notification in your inbox.",
        "Any address you gave for a home visit.",
        "Upcoming appointments, which are cancelled.",
      ],
    },
    {
      title: "What the clinic keeps",
      items: [
        "Appointments you actually had. A clinic needs its own record of who it saw, and your name is removed from it.",
        "The star rating on any review you left, without the words. Ratings stay so a business’s public average cannot be changed after the fact.",
        // H42: a note can name the person, so "we deleted your account" must
        // not be read as "you are gone from Bookr".
        "Notes a clinic wrote about you, no longer linked to your account. They are that clinic’s record of treating you; ask them if you want them removed.",
        // H53: the same reasoning for a meal plan a clinic gave.
        "Meal plans a clinic gave you, no longer linked to your account. They are that clinic’s record of what it advised; ask them if you want them removed.",
      ],
    },
  ],
  finalLine: "This cannot be undone, and there is no waiting period.",
};

/**
 * A Business Member's (H58): they leave every clinic as if a manager had
 * removed them (J2#5), and the clinic keeps its record of their appointments.
 */
export const MEMBER_DELETION: DeletionCopy = {
  summary: "Appointments already booked with you stay with the clinic.",
  sections: [
    {
      title: "What happens",
      items: [
        "You leave every clinic you work at, as if a manager had removed you.",
        "Appointments already booked with you stay with the clinic.",
        "A registration waiting for review is withdrawn.",
      ],
    },
  ],
  finalLine: "This cannot be undone.",
};
